import React, { PureComponent } from 'react';

import { DndProvider, DragSource, DropTarget } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import reactStringReplace from 'react-string-replace';
import { canIUse, AppFeatures } from '../../helpers/PermissionUtility';
import { showPrompt } from '../../helpers/MessageUtility';
import PopupDropdown from '../../components/PopupDropdown';
import { TagsItem } from '../tags-types';

export type TagItemProps = {
  tag: TagsItem;
  selectTag: (id: string, name: string) => void;
  level: number;
  expandedItems: string[];
  toggleExpanded: (id: string) => void;
  onRemove: (id: string) => void;
  onRename: (id: string, text: string, parentId: string) => void;
  onMove: (draggedId: string, name: string, droppedId: string) => void;
  parentId: string;
  parentIds: string[];
  connectDragSource?: AnyFunc;
  connectDropTarget?: AnyFunc;
  isOver?: boolean;
  canDrop?: boolean;
  highlight: string;
};

const dragSource = {
  beginDrag(props) {
    return {
      tag: props.tag,
      parentId: props.parentId
    };
  }
};

const dropTarget = {
  drop(props: TagItemProps, monitor) {
    const draggedItem = monitor.getItem();
    const draggedTag = draggedItem.tag;
    const dropTag = props.tag;
    props.onMove(draggedTag.id, draggedTag.name, dropTag.id);
  },
  canDrop(props: TagItemProps, monitor) {
    const draggedItem = monitor.getItem();
    const draggedTagId = draggedItem.tag.id;
    const dropTagId = props.tag.id;

    if (props.parentIds.indexOf(draggedTagId) > -1 || draggedTagId === dropTagId) {
      return false;
    }
    return true;
  }
};
//@ts-ignore - FIX
@DropTarget('tagitem', dropTarget, (c, monitor) => ({
  connectDropTarget: c.dropTarget(),
  isOver: monitor.isOver(),
  canDrop: monitor.canDrop()
}))
//@ts-ignore - FIX
@DragSource('tagitem', dragSource, (c, monitor) => ({
  connectDragSource: c.dragSource(),
  isDragging: monitor.isDragging()
}))
export default class TagItem extends PureComponent<TagItemProps, TagItem> {
  private div: HTMLDivElement;

  constructor(props: TagItemProps) {
    super(props);

    this.toggleExpanded = this.toggleExpanded.bind(this);
    this.rename = this.rename.bind(this);
  }

  toggleExpanded() {
    this.props.toggleExpanded(this.props.tag.id);
  }

  isExpanded() {
    return this.props.expandedItems.includes(this.props.tag.id);
  }

  rename() {
    const { tag, parentId, onRename } = this.props;
    showPrompt(
      {
        title: 'Rename Tag',
        msg: 'Tag name',
        text: tag.name,
        maxLength: 50
      },
      (result, text) => {
        if (result) {
          onRename(tag.id, text, parentId);
        }
      }
    );
  }

  render() {
    const { tag, selectTag, level, connectDragSource, connectDropTarget, isOver, parentId, canDrop, highlight } = this.props;
    const expanded = this.isExpanded();
    const canManageTags = canIUse(AppFeatures.TagManage);
    const canAddTags = canIUse(AppFeatures.TagAdd);

    const children = tag.children;
    let icon;
    if (children?.length > 0) {
      icon = (
        <button className="plain-button expand-icon" onClick={this.toggleExpanded}>
          <i className={`icon angle ${expanded ? 'down' : 'right'}`} />
        </button>
      );
    } else {
      icon = <i className="icon tag" />;
    }

    let tagNameRender = null;
    if (highlight) {
      tagNameRender = reactStringReplace(tag.name, highlight, (match) => <span className="highlight">{match}</span>);
    } else {
      tagNameRender = tag.name;
    }

    const tagLink = canAddTags ? (
      <div className="tag-link">
        <a onClick={() => selectTag(tag.id, tag.name)}>{tagNameRender}</a>
      </div>
    ) : (
      <div className="tag-link">{tagNameRender}</div>
    );

    const tagItem = canManageTags ? (
      <div
        key={tag.id}
        className={`tag-item${canManageTags ? ' draggable' : ''}${isOver && canDrop ? ' over' : ''}`}
        ref={(r) => {
          this.div = r;
        }}
        style={{ paddingLeft: `${2 + level * 16}px` }}
      >
        {icon}
        {tagLink}
        <PopupDropdown
          trigger={
            <button className="plain-button menu-button">
              <i className="ellipsis horizontal icon" />
            </button>
          }
          content={
            <PopupDropdown.Menu vertical>
              {parentId ? (
                <PopupDropdown.Item
                  text="Move to root"
                  icon="move"
                  onClick={() => {
                    this.props.onMove(tag.id, tag.name, null);
                  }}
                />
              ) : null}
              <PopupDropdown.Item text="Rename" icon="edit" onClick={this.rename} />
              <PopupDropdown.Item
                text="Delete"
                icon="remove"
                onClick={() => {
                  this.props.onRemove(tag.id);
                }}
              />
            </PopupDropdown.Menu>
          }
        />
      </div>
    ) : (
      <div key={tag.id} className="tag-item" style={{ paddingLeft: `${2 + level * 16}px` }}>
        {icon}
        {tagLink}
      </div>
    );

    return canManageTags ? <DndProvider backend={HTML5Backend}>{connectDropTarget(connectDragSource(tagItem))}</DndProvider> : tagItem;
  }
}
